import React from 'react';
import {
  Mail,
  Rocket,
  Heart,
  Zap,
  Briefcase,
  CheckCircle,
  Globe,
  ArrowRight,
  Star,
  Users,
  Target,
  ChevronRight
} from 'lucide-react';

const Careers = () => {
  const benefits = [
    {
      icon: Rocket,
      title: "Rapid Growth",
      desc: "We don't just teach growth; we live it. Expect to level up your skills faster than anywhere else."
    },
    {
      icon: Zap,
      title: "High Impact",
      desc: "Your work directly changes lives. Help us empower thousands of people to reach their potential."
    },
    {
      icon: Heart,
      title: "Culture of Excellence",
      desc: "Surround yourself with high-performers who push you to be your absolute best."
    },
    {
      icon: Globe,
      title: "Remote Friendly",
      desc: "We care about results, not hours in a chair. Many roles offer flexible working arrangements."
    }
  ];

  const openings = [
    { title: "Sales Executive", type: "Full-Time", location: "Patna / Remote", dept: "Sales", tags: ["Commission", "High Growth"] },
    { title: "Content Creator", type: "Part-Time", location: "Remote", dept: "Marketing", tags: ["Creative", "Flexible"] },
    { title: "Performance Coach", type: "Contract", location: "Hybrid", dept: "Training", tags: ["Expert Level", "Impact"] }
  ];

  return (
    <div className="bg-white min-h-screen font-sans text-gray-900">
      {/* Hero Section - Light Theme */}
      <div className="relative bg-white overflow-hidden pt-20 pb-24 lg:pt-32 lg:pb-40">
        {/* Background Decor */}
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 bg-purple-50 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-pulse"></div>
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-96 h-96 bg-yellow-50 rounded-full mix-blend-multiply filter blur-3xl opacity-70"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-purple-50 border border-purple-100 px-4 py-1.5 rounded-full mb-8 animate-fade-in-up">
              <Star className="w-4 h-4 text-exiser-primary fill-current" />
              <span className="text-xs font-bold uppercase tracking-widest text-exiser-primary">We Are Hiring</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold uppercase mb-6 tracking-tight text-gray-900 leading-tight animate-fade-in-up delay-100">
              Build A <span className="text-transparent bg-clip-text bg-gradient-to-r from-exiser-primary to-purple-600">Legacy</span>
            </h1>
            <p className="text-xl text-gray-600 font-light leading-relaxed mb-10 animate-fade-in-up delay-200">
              Don't just get a job. Join a movement. We are redefining human potential, one breakthrough at a time.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up delay-300">
              <a
                href="#openings"
                className="inline-flex items-center justify-center gap-2 bg-exiser-primary text-white px-8 py-4 font-bold uppercase tracking-wider hover:bg-purple-900 transition-all shadow-xl shadow-purple-100 rounded-lg hover:-translate-y-1"
              >
                View Openings <ArrowRight className="w-5 h-5" />
              </a>
              <a
                href="#contact"
                className="inline-flex items-center justify-center gap-2 bg-white text-gray-700 border border-gray-200 px-8 py-4 font-bold uppercase tracking-wider hover:bg-gray-50 transition-all rounded-lg"
              >
                Contact Team
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Culture / DNA Section */}
      <div className="bg-gray-50/50 py-24 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold uppercase text-gray-900">Our DNA</h2>
            <div className="w-20 h-1.5 bg-exiser-accent mx-auto mt-4 rounded-full"></div>
            <p className="text-gray-500 mt-6 max-w-2xl mx-auto text-lg font-light">
              We are not for everyone. We are for the driven, the obsessed, and the visionary.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((item, idx) => (
              <div
                key={idx}
                className="p-8 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-xl hover:border-purple-100 hover:-translate-y-1 transition-all duration-300 group"
              >
                <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center text-exiser-primary mb-6 group-hover:bg-exiser-primary group-hover:text-white transition-all duration-300">
                  <item.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold uppercase text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Open Positions */}
      <div id="openings" className="bg-white py-24">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-end mb-12 border-b border-gray-100 pb-6">
            <div>
              <h2 className="text-3xl font-bold uppercase text-gray-900 mb-2">Current Opportunities</h2>
              <p className="text-gray-500">Find your role and make your mark.</p>
            </div>
            <div className="mt-4 md:mt-0">
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase bg-green-50 text-green-700 px-3 py-1 rounded-full border border-green-100">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                {openings.length} Positions Active
              </span>
            </div>
          </div>

          <div className="space-y-4">
            {openings.map((job, idx) => (
              <div
                key={idx}
                className="group bg-white border border-gray-200 rounded-xl p-6 md:p-8 hover:border-exiser-primary hover:shadow-lg transition-all duration-300 cursor-pointer relative overflow-hidden"
              >
                <div className="absolute left-0 top-0 h-full w-1 bg-exiser-primary opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold text-gray-900 uppercase">{job.title}</h3>
                      {idx === 0 && (
                        <span className="bg-exiser-accent/20 text-exiser-primary text-[10px] font-bold uppercase px-2 py-0.5 rounded border border-exiser-accent/30">
                          Hot
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500 font-medium">
                      <span className="flex items-center gap-1.5">
                        <Briefcase className="w-4 h-4 text-gray-400" /> {job.type}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Globe className="w-4 h-4 text-gray-400" /> {job.location}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Users className="w-4 h-4 text-gray-400" /> {job.dept}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 w-full md:w-auto justify-between md:justify-end">
                    <div className="flex gap-2">
                      {job.tags?.map((tag, tIdx) => (
                        <span
                          key={tIdx}
                          className="bg-gray-50 text-gray-600 px-3 py-1 rounded-lg text-xs font-bold uppercase tracking-wide"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <a
                      href="mailto:admin@exiser.in"
                      className="hidden md:flex items-center gap-2 text-exiser-primary font-bold uppercase text-sm hover:translate-x-1 transition-transform"
                    >
                      Apply <ChevronRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>
                {/* Mobile Apply Button */}
                <a
                  href="mailto:admin@exiser.in"
                  className="md:hidden mt-6 block w-full text-center bg-gray-900 text-white py-3 rounded-lg font-bold uppercase text-sm"
                >
                  Apply Now
                </a>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Resume Drop - Light Professional Style */}
      <div id="contact" className="py-24 bg-gray-50 border-t border-gray-200">
        <div className="max-w-3xl mx-auto px-4 relative z-10">
          <div className="bg-white rounded-3xl p-8 md:p-12 text-center shadow-xl border border-gray-100 relative overflow-hidden">
            {/* Decorative blobs */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-purple-50 rounded-full mix-blend-multiply filter blur-3xl opacity-50 -mr-16 -mt-16 pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-yellow-50 rounded-full mix-blend-multiply filter blur-3xl opacity-50 -ml-16 -mb-16 pointer-events-none"></div>

            <div className="relative z-10">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-2xl mb-6 text-exiser-primary rotate-6 shadow-sm">
                <Mail className="w-8 h-8" />
              </div>

              <h2 className="text-3xl font-bold uppercase text-gray-900 mb-4">Don't See Your Role?</h2>
              <p className="text-gray-600 mb-8 text-lg font-light">
                We are always looking for exceptional talent. If you believe you have what it takes to dominate in your
                field, pitch us directly.
              </p>

              <div className="bg-gray-50 rounded-xl p-6 border border-gray-100 inline-block w-full max-w-md mx-auto">
                <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">Send Your Resume To</p>
                <a
                  href="mailto:admin@exiser.in"
                  className="text-2xl md:text-3xl font-bold text-exiser-primary hover:text-purple-700 transition-colors block mb-4"
                >
                  admin@exiser.in
                </a>
                <div className="flex justify-center gap-4 text-xs font-bold uppercase text-gray-500">
                  <span className="flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 text-green-500" /> 48 Hr Response
                  </span>
                  <span className="flex items-center gap-1">
                    <Target className="w-3 h-3 text-blue-500" /> Direct Team
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Careers;
